import { AuthContext, useUserAuthState, useUserRoutes } from '@cosuno/authentication-frontend';
import { PageLoader } from '@cosuno/cosuno-ui';
import React, { type PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import PageNotFound from '~/components/PageNotFound';
import { toast } from '~/packages/Toast';

import useRoutePath from '../useRoutePath';

const emptyParams = {};

const WorkspaceRedirect: React.FC<
  PropsWithChildren<{ Page: React.ComponentType<PropsWithChildren<{ renderLayout?: boolean }>> }>
> = ({ children, Page }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // we can't use useParams hook because the workspaceId is optional and omitted due to some
  // weird implementation detail of route resolution
  const routePath = useRoutePath();
  const match = useMatch(routePath ?? '');
  const params: { workspaceId?: string } = match ? match.params : emptyParams;

  const { currentUser, isUserAuthenticated } = useUserAuthState();
  const [isProcessed, setIsProcessed] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const { switchToWorkspace } = useContext(AuthContext);
  const routes = useUserRoutes();

  const processedWorkspaceIdRef = useRef<string | undefined>(undefined);
  const processedUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    setIsNotFound(false);
  }, [location]);

  useEffect(() => {
    const switchToWorkspaceOrDisplayToast = async (workspaceId: string, redirectTo: string) => {
      try {
        await switchToWorkspace(workspaceId, redirectTo);
      } catch (error) {
        toast.error(error);
      }
    };

    if ('workspaceId' in params) {
      // not generating path from match.path and match.params because of issues with how this
      // works with our tabs (e.g. company() route doesn't have :tab param)
      const currentPath = `${location.pathname}${location.search}`;

      // When the workspace changes, we respond by logging in the user to the new workspace.
      // When the user changes, we respond by resetting the current workspace (redirecting to
      // the root page).
      // It's important to distinguish between these two cases because otherwise we may get
      // infinite loops between browser tabs when the user is synced via local storage.

      const { workspaceId: currentWorkspaceId } = params;
      const processedWorkspaceId = processedWorkspaceIdRef.current;

      const workspaceIdChanged = processedWorkspaceId !== currentWorkspaceId;
      processedWorkspaceIdRef.current = currentWorkspaceId;

      const currentUserId = currentUser?.id;
      const processedUserId = processedUserIdRef.current;

      const userChanged = processedUserId !== currentUserId;
      processedUserIdRef.current = currentUserId;

      if (!currentWorkspaceId) {
        const workspace = currentUser?.workspaces.find(({ isCurrent }) => isCurrent);

        if (workspace) {
          const redirectTo = `/${workspace.id}${currentPath}`;

          navigate(redirectTo, { state: location.state, replace: true });
          return;
        }
      } else if (workspaceIdChanged) {
        const workspace = currentUser?.workspaces.find(({ id }) => id === currentWorkspaceId);

        if (workspace) {
          if (!workspace.isCurrent) {
            void switchToWorkspaceOrDisplayToast(workspace.id, currentPath);
            return;
          }
        } else {
          setIsNotFound(true);
        }
      } else if (userChanged && currentUser && processedUserId && currentWorkspaceId) {
        const workspace = currentUser.workspaces.find(({ id }) => id === currentWorkspaceId);
        if (!workspace || !workspace.isCurrent) {
          navigate(routes.root());
        }
      }
    }

    setIsProcessed(true);
  }, [setIsProcessed, location, currentUser, switchToWorkspace, routes, params, navigate]);

  if (!isProcessed) {
    return <PageLoader />;
  }

  if (isNotFound) {
    return isUserAuthenticated ? (
      <Page renderLayout>
        <PageNotFound.Authenticated />
      </Page>
    ) : (
      <Page>
        <PageNotFound.Unauthenticated />
      </Page>
    );
  }

  return <>{children}</>;
};

export default WorkspaceRedirect;
